function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function formatDate(value) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('nb-NO', { dateStyle: 'long' }).format(date);
}

function reportFailure(onFailure, message) {
  if (onFailure) onFailure(message);
}

export function printAcademyCertificate(certificate, onFailure) {
  if (!certificate) {
    reportFailure(onFailure, 'Kursbeviset er ikke tilgjengelig for utskrift.');
    return false;
  }

  const popup = window.open('', '_blank');

  if (!popup) {
    reportFailure(
      onFailure,
      'Nettleseren blokkerte utskriftsfanen. Tillat popup-vinduer for HMS-innsikt, og prøv igjen.'
    );
    return false;
  }

  const revoked = certificate.status === 'revoked';
  const statusText = revoked
    ? 'Tilbakekalt'
    : certificate.status === 'issued'
      ? 'Utstedt'
      : certificate.status || 'Ukjent';

  const version = [
    certificate.course_version_version,
    certificate.course_version_language_code
      ? certificate.course_version_language_code.toUpperCase()
      : ''
  ].filter(Boolean).join(' · ');

  const fields = [
    ['Kursversjon', version],
    ['Fullført', formatDate(certificate.completion_completed_at)],
    ['Utstedt', formatDate(certificate.issued_at)],
    ['Gyldig til', certificate.valid_until
      ? formatDate(certificate.valid_until)
      : 'Ingen utløpsdato'],
    ['Sertifikatnummer', certificate.certificate_number],
    ['Status', revoked ? 'Tilbakekalt — ikke gyldig' : statusText]
  ];

  const fieldMarkup = fields.map(([label, value]) => `
    <div class="field">
      <dt>${escapeHtml(label)}</dt>
      <dd>${escapeHtml(value || '—')}</dd>
    </div>
  `).join('');

  const documentMarkup = `<!doctype html>
<html lang="nb">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>Kursbevis – ${escapeHtml(certificate.certificate_number)}</title>
  <style>
    * { box-sizing: border-box; }
    html, body { margin: 0; min-height: 100%; }
    body {
      background: #eef4fa;
      color: #183153;
      font-family: Arial, Helvetica, sans-serif;
    }
    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #dce5ef;
      background: #fff;
    }
    .toolbar-brand { color: #163f8c; font-weight: 700; }
    .toolbar-note { margin-top: .25rem; color: #52657d; font-size: .9rem; }
    .toolbar button {
      min-height: 2.65rem;
      padding: .65rem 1rem;
      border: 1px solid #2563eb;
      border-radius: .65rem;
      background: #2563eb;
      color: #fff;
      font-weight: 700;
      cursor: pointer;
    }
    .stage { display: flex; justify-content: center; padding: 2rem; }
    .paper {
      width: min(100%, 1120px);
      aspect-ratio: 297 / 210;
      min-height: 600px;
      padding: 1.2rem;
      background: #fff;
      box-shadow: 0 18px 50px rgba(22, 63, 140, .12);
    }
    .border {
      display: flex;
      height: 100%;
      flex-direction: column;
      justify-content: space-between;
      border: 2px solid #d5e4f3;
      outline: 1px solid #edf3f9;
      outline-offset: -.5rem;
      padding: clamp(1.4rem, 4vw, 3.5rem);
    }
    .topline, .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }
    .brand { color: #163f8c; font-size: 1.1rem; font-weight: 800; }
    .status {
      border: 1px solid ${revoked ? '#e9b4b8' : '#a8d8bd'};
      border-radius: 999px;
      background: ${revoked ? '#fff1f1' : '#edf8f1'};
      color: ${revoked ? '#a3333d' : '#17653b'};
      padding: .4rem .85rem;
      font-size: .78rem;
      font-weight: 700;
    }
    .heading { padding: 1rem 0 1.6rem; text-align: center; }
    .eyebrow {
      color: #2563eb;
      font-size: .8rem;
      font-weight: 800;
      letter-spacing: .24em;
      text-transform: uppercase;
    }
    h1 { margin: .5rem 0 0; color: #163f8c; font-size: 2.5rem; }
    .intro, .completed, .version-title { color: #52657d; }
    .intro { margin-top: 1.4rem; }
    .learner { margin: .25rem 0 0; color: #163f8c; font-size: 2.2rem; font-weight: 700; }
    .completed { margin-top: .45rem; }
    h2 { margin: .8rem 0 0; color: #183153; font-size: 1.5rem; }
    .version-title { margin-top: .25rem; font-size: .9rem; }
    dl {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1rem 2rem;
      border-top: 1px solid #dce5ef;
      padding-top: 1.2rem;
    }
    dt {
      color: #718096;
      font-size: .72rem;
      font-weight: 700;
      letter-spacing: .04em;
      text-transform: uppercase;
    }
    dd { margin: .25rem 0 0; font-size: .95rem; font-weight: 700; overflow-wrap: anywhere; }
    .footer { border-top: 1px solid #dce5ef; padding-top: 1rem; }
    .footer-label { display: block; margin-bottom: .2rem; color: #718096; font-size: .72rem; }
    .issuer, .revocation { color: #163f8c; font-weight: 700; }
    .revocation { color: #a3333d; letter-spacing: .12em; }
    @page { size: A4 landscape; margin: 10mm; }
    @media print {
      *,
      *::before,
      *::after {
        box-sizing: border-box !important;
      }
      html, body {
        width: auto;
        height: auto;
        min-height: 0;
        margin: 0;
        overflow: visible;
        background: #fff;
      }
      .toolbar { display: none !important; }
      .stage {
        display: block;
        width: 100%;
        height: auto;
        margin: 0;
        padding: 0;
        break-inside: avoid-page;
        break-after: auto;
        page-break-inside: avoid;
        page-break-after: auto;
      }
      .paper {
        width: 100%;
        height: 174mm;
        min-height: 0;
        margin: 0;
        padding: 4mm;
        aspect-ratio: auto;
        overflow: visible;
        box-shadow: none;
        break-inside: avoid-page;
        break-after: auto;
        page-break-inside: avoid;
        page-break-after: auto;
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
      .border {
        box-sizing: border-box;
        height: 100%;
        min-height: 0;
        padding: 10mm;
        break-inside: avoid-page;
        page-break-inside: avoid;
      }
      .heading { padding: 4mm 0 6mm; }
      dl { gap: 5mm 10mm; }
    }
  </style>
</head>
<body>
  <header class="toolbar">
    <div>
      <div class="toolbar-brand">HMS-innsikt · Kursbevis</div>
      <div class="toolbar-note">Hvis utskriftsdialogen ikke åpnes, bruk knappen eller Ctrl+P / Cmd+P.</div>
    </div>
    <button id="print-certificate" type="button">Skriv ut</button>
  </header>
  <main class="stage">
    <article class="paper" aria-label="Kursbevis">
      <div class="border">
        <div class="topline">
          <span class="brand">HMS-innsikt</span>
          <span class="status">${escapeHtml(statusText)}</span>
        </div>
        <section class="heading">
          <p class="eyebrow">Kursbevis</p>
          <h1>${escapeHtml(certificate.certificate_display_title || 'Kursbevis')}</h1>
          <p class="intro">Dette bekrefter at</p>
          <p class="learner">${escapeHtml(certificate.learner_display_name)}</p>
          <p class="completed">har fullført kurset</p>
          <h2>${escapeHtml(certificate.course_title)}</h2>
          ${certificate.course_version_title
            ? `<p class="version-title">${escapeHtml(certificate.course_version_title)}</p>`
            : ''}
        </section>
        <dl>${fieldMarkup}</dl>
        <footer class="footer">
          <div>
            <span class="footer-label">Utstedt av</span>
            <strong class="issuer">${escapeHtml(certificate.issuer_display_name || 'HMS-innsikt')}</strong>
          </div>
          ${revoked ? '<strong class="revocation">TILBAKEKALT</strong>' : ''}
        </footer>
      </div>
    </article>
  </main>
</body>
</html>`;

  try {
    popup.document.open();
    popup.document.write(documentMarkup);
    popup.document.close();

    const printButton = popup.document.getElementById('print-certificate');
    printButton?.addEventListener('click', () => popup.print());

    popup.opener = null;
    popup.focus();
    popup.print();
    return true;
  } catch {
    popup.close();
    reportFailure(
      onFailure,
      'Utskriften kunne ikke startes. Prøv igjen, eller åpne utskriftsfanen og bruk Ctrl+P / Cmd+P.'
    );
    return false;
  }
}